import { extendTheme } from "@chakra-ui/react";

// Kumkum ledger: the red cloth-bound account book of a priest's office.
// Turmeric only ever appears as a fill with ink text, it fails contrast as text.
// Column headers carry the book's red ruled line: a kumkum tint with a 2px kumkum rule.
const colors = {
  kumkum: {
    50: "#FAECEE",
    100: "#F1CFD4",
    200: "#E0A0AA",
    300: "#C96C7B",
    400: "#A83F52",
    500: "#7A1626",
    600: "#6A1221",
    700: "#570E1B",
    800: "#430A14",
    900: "#2E060D",
  },
  turmeric: { 100: "#FCEFC7", 300: "#F4CF62", 500: "#E9A80C", 700: "#7A5804" },
  tulsi: { 100: "#DDEBE3", 500: "#2F6B4F", 700: "#234F3B" },
  stone: { 50: "#F7F8F9", 100: "#EEF0F2", 200: "#DFE2E6", 300: "#C6CBD1", 500: "#6B6468", 700: "#4A4347" },
  ink: "#231A1E",
};

const theme = extendTheme({
  colors,
  fonts: {
    heading: "'Yatra One', Georgia, serif",
    body: "'Anek Telugu', 'Segoe UI', sans-serif",
  },
  radii: { sm: "3px", md: "4px", lg: "6px" },
  styles: {
    global: {
      body: { bg: "stone.100", color: "ink", fontVariantNumeric: "tabular-nums" },
      "*:focus-visible": { outline: "2px solid", outlineColor: "kumkum.500", outlineOffset: "2px" },
    },
  },
  components: {
    Heading: {
      baseStyle: { color: "kumkum.500", fontWeight: 400 },
    },
    Button: {
      baseStyle: { fontFamily: "body", fontWeight: 600, borderRadius: "md" },
      defaultProps: { variant: "solid", colorScheme: "kumkum" },
      variants: {
        solid: { bg: "kumkum.500", color: "white", _hover: { bg: "kumkum.600", _disabled: { bg: "kumkum.500" } }, _active: { bg: "kumkum.700" } },
        // Borders stay at 3:1 or better against white so every button reads as a button.
        outline: {
          bg: "white",
          color: "ink",
          border: "1px solid",
          borderColor: "stone.500",
          _hover: { bg: "kumkum.50", borderColor: "kumkum.500", color: "kumkum.700" },
          _active: { bg: "kumkum.100" },
        },
        ghost: { color: "ink", _hover: { bg: "stone.200" }, _active: { bg: "stone.300" } },
        danger: {
          bg: "white",
          color: "#A61B1B",
          border: "1px solid",
          borderColor: "#A61B1B",
          _hover: { bg: "#FBEAEA" },
          _active: { bg: "#F5D3D3" },
        },
        dangerSolid: { bg: "#A61B1B", color: "white", _hover: { bg: "#8A1414" }, _active: { bg: "#701010" } },
      },
    },
    FormLabel: {
      baseStyle: { fontSize: "sm", fontWeight: 600, color: "stone.700", mb: 1 },
    },
    Input: {
      defaultProps: { focusBorderColor: "kumkum.500" },
      variants: { outline: { field: { bg: "white", borderColor: "stone.300", _hover: { borderColor: "stone.500" } } } },
    },
    Select: {
      defaultProps: { focusBorderColor: "kumkum.500" },
      variants: { outline: { field: { bg: "white", borderColor: "stone.300", _hover: { borderColor: "stone.500" } } } },
    },
    Textarea: {
      defaultProps: { focusBorderColor: "kumkum.500" },
      variants: { outline: { bg: "white", borderColor: "stone.300", _hover: { borderColor: "stone.500" } } },
    },
    Switch: {
      defaultProps: { colorScheme: "kumkum" },
    },
    Table: {
      variants: {
        simple: {
          table: { bg: "white" },
          th: {
            fontFamily: "body",
            textTransform: "none",
            letterSpacing: 0,
            fontSize: "md",
            fontWeight: 600,
            color: "kumkum.700",
            bg: "kumkum.50",
            borderColor: "stone.200",
          },
          // Only column headers get the red rule, not row labels in a tbody.
          thead: { "& th": { borderBottom: "2px solid", borderColor: "kumkum.500" } },
          td: { borderColor: "stone.200", py: 3 },
        },
      },
    },
    Drawer: {
      baseStyle: {
        overlay: { bg: "rgba(35, 26, 30, 0.45)" },
        dialog: { bg: "white" },
        header: {
          fontFamily: "heading",
          fontWeight: 400,
          fontSize: "2xl",
          color: "kumkum.500",
          borderBottom: "1px solid",
          borderColor: "stone.200",
        },
        body: { py: 5 },
        footer: { borderTop: "1px solid", borderColor: "stone.200", gap: 2 },
      },
    },
    Modal: {
      baseStyle: {
        overlay: { bg: "rgba(35, 26, 30, 0.45)" },
        header: { fontFamily: "heading", fontWeight: 400, color: "kumkum.500" },
      },
    },
    Accordion: {
      baseStyle: { container: { borderColor: "stone.200" } },
    },
  },
});

export default theme;
